import React, { createContext, useContext, useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { Amplify } from "aws-amplify";
import {
  getCurrentUser,
  fetchUserAttributes,
  updateUserAttributes,
  signIn,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  SignInInput,
  AuthError,
  SignInOutput,
} from "aws-amplify/auth";
import { QRCodeSVG as QRCode } from "qrcode.react";
import awsconfig from "~/aws-exports";
import { initializeCustomerRecords } from "~/utils/dynamodbInitializer";
import { customerIdAtom } from "~/AtomWithDynamoDB";
import { OTPInput } from "~/components/auth/OTPInput";
import "~/css/CognitoAuth.css";

// 暫定: システム名を定義
const systemName = "おもてなしAI";

// Amplify設定を初期化
Amplify.configure(awsconfig);

// ユーザー型定義
interface CognitoUser {
  username: string;
  userId: string;
  name?: string;
  signInDetails?: Record<string, unknown>;
}

// エラーメッセージ変換ヘルパー
const getResetPasswordErrorMessage = (error: Error & { name: string }): string => {
  if (error.name === "UserNotFoundException" || error.message.includes("UserNotFoundException")) {
    return "このメールアドレスで登録されたユーザーは見つかりません";
  }
  if (
    error.name === "InvalidParameterException" ||
    error.message.includes("InvalidParameterException")
  ) {
    return "無効なメールアドレス形式です";
  }
  if (error.name === "LimitExceededException" || error.message.includes("LimitExceededException")) {
    return "リクエストが多すぎます。しばらく時間をおいてから再試行してください";
  }
  if (error.name === "NotAuthorizedException" || error.message.includes("NotAuthorizedException")) {
    return "このアカウントはパスワードリセットが許可されていません";
  }
  return error.message;
};

// メールアドレスバリデーション
const validateEmail = (email: string): string | null => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return "有効なメールアドレスを入力してください";
  }
  return null;
};

// 認証ステップの型定義
type AuthStep =
  | "SIGN_IN"
  | "NEW_PASSWORD_REQUIRED"
  | "FORGOT_PASSWORD"
  | "RESET_PASSWORD"
  | "MFA_SETUP"
  | "CONFIRM_SIGN_IN_WITH_TOTP"
  | "AUTHENTICATED";

// 認証コンテキストの型定義
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CognitoUser | null;
  authStep: AuthStep;
  signIn: (username: string, password: string) => Promise<void>;
  confirmNewPassword: (newPassword: string, name?: string) => Promise<void>;
  resetPassword: (username: string) => Promise<void>;
  confirmResetPassword: (
    username: string,
    confirmationCode: string,
    newPassword: string
  ) => Promise<void>;
  confirmSignInWithTOTP: (totpCode: string) => Promise<void>;
  verifyTOTPSetup: (totpCode: string) => Promise<void>;
  updateName: (name: string) => Promise<void>;
  signOut: () => Promise<void>;
  setAuthStep: (step: AuthStep) => void;
  error: string | null;
  resetPasswordDeliveryDetails: string | null;
  totpSetupUrl: string | null;
}

// 認証コンテキストを作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 認証プロバイダーコンポーネント
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [authStep, setAuthStep] = useState<AuthStep>("SIGN_IN");
  const [error, setError] = useState<string | null>(null);
  const [signInResult, setSignInResult] = useState<SignInOutput | null>(null);
  const [resetPasswordDeliveryDetails, setResetPasswordDeliveryDetails] = useState<string | null>(
    null
  );
  const [totpSetupUrl, setTotpSetupUrl] = useState<string | null>(null);
  const setCustomerId = useSetAtom(customerIdAtom);

  // 現在のユーザー状態をチェック
  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      console.info("Checking auth state...");
      const currentUser = await getCurrentUser();
      // console.info("Current user:", currentUser);
      const attributes = await fetchUserAttributes();
      setUser({ ...(currentUser as CognitoUser), name: attributes.name });
      setIsAuthenticated(true);
      setAuthStep("AUTHENTICATED");

      // customerIdAtomを更新
      setCustomerId(currentUser.userId);

      console.info("User authenticated successfully");

      // 認証成功後にカスタマーレコードを初期化
      try {
        console.info("Initializing customer records...");
        await initializeCustomerRecords(currentUser.userId);
        console.info("Customer records initialization completed");
      } catch (initError) {
        console.error("Failed to initialize customer records:", initError);
        // レコード初期化エラーは認証を妨げない
      }
    } catch (error) {
      console.info("User not authenticated:", error);
      setUser(null);
      setIsAuthenticated(false);
      setAuthStep("SIGN_IN");

      // 認証されていない場合はcustomerIdAtomをクリア
      setCustomerId(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * サインイン後の次ステップを処理
   * Cognitoから返されるnextStepに応じて適切な画面に遷移する
   * @param result SignInOutput サインイン結果オブジェクト
   * @returns void
   */
  const handleSignInNextStep = async (result: SignInOutput) => {
    const nextStepType = result.nextStep?.signInStep;

    if (nextStepType === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      // 初回ログインまたは管理者によるパスワードリセット後の場合、
      // 新しいパスワードの設定を要求
      setAuthStep("NEW_PASSWORD_REQUIRED");
    } else if (nextStepType === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
      // MFA未設定のユーザーが初めてログインした場合、
      // Authenticatorアプリでスキャンするための二次元コードを表示
      // 二次元コード用のURIを生成（otpauth://totp/...形式）
      const setupUri = result.nextStep?.totpSetupDetails?.getSetupUri(systemName);
      if (setupUri) {
        setTotpSetupUrl(setupUri.href);
      }
      setAuthStep("MFA_SETUP");
    } else if (nextStepType === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      // MFA設定済みのユーザーがログインした場合、
      // Authenticatorアプリの6桁コードの入力を要求
      setAuthStep("CONFIRM_SIGN_IN_WITH_TOTP");
    } else {
      // 追加の認証ステップが不要(通常のログイン完了)
      // 認証状態を再確認してメイン画面へ遷移
      await checkAuthState();
    }
  };

  // サインイン処理
  const handleSignIn = async (username: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);

      const signInInput: SignInInput = {
        username,
        password,
      };

      console.info("Attempting sign in...");
      const result = await signIn(signInInput);
      console.info("Sign in result:", result);
      setSignInResult(result);

      // サインイン後の次ステップを処理
      await handleSignInNextStep(result);
    } catch (error: unknown) {
      console.error("Sign in error:", error);
      if (error instanceof AuthError) {
        setError(error.message);
      } else {
        setError("サインインに失敗しました");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 新しいパスワードの確認
  const handleConfirmNewPassword = async (newPassword: string, name?: string) => {
    try {
      setError(null);
      setIsLoading(true);

      if (!signInResult) {
        throw new Error("サインイン結果がありません");
      }

      console.info("Confirming new password...");

      // AWS Amplify v6の新しいパスワード設定
      // name属性が必要な場合があるため、optionsで提供
      await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name: name || "管理者", // 提供されたname、または デフォルト値を使用
          },
        },
      });

      console.info("New password confirmed, checking auth state...");
      await checkAuthState();
    } catch (error: unknown) {
      console.error("Confirm new password error:", error);
      if (error instanceof AuthError) {
        setError(error.message);
      } else {
        setError("パスワード設定に失敗しました");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 名前(name属性)更新処理
  const handleUpdateName = async (name: string) => {
    await updateUserAttributes({ userAttributes: { name } });
    setUser((prev) => (prev ? { ...prev, name } : prev));
  };

  // サインアウト処理
  const handleSignOut = async () => {
    try {
      setError(null);
      await signOut();
      setUser(null);
      setIsAuthenticated(false);

      // customerIdAtomをクリア
      setCustomerId(null);
    } catch (error) {
      console.error("Sign out error:", error);
      setError("サインアウトに失敗しました");
    }
  };

  // 認証ステップの設定
  const setAuthStepHandler = (step: AuthStep) => {
    setAuthStep(step);
    setError(null); // ステップ変更時にエラーをクリア
  };

  // パスワードリセット開始処理
  const handleResetPassword = async (username: string) => {
    try {
      setError(null);
      setIsLoading(true);

      // メールアドレスの基本バリデーション
      const validationError = validateEmail(username);
      if (validationError) {
        setError(validationError);
        setIsLoading(false);
        return;
      }

      console.info("Initiating password reset...");
      const result = await resetPassword({ username });
      console.info("Password reset result:", result);

      // リセットコードの送信先を保存
      const deliveryDetails = result.nextStep?.codeDeliveryDetails;
      if (deliveryDetails?.destination) {
        setResetPasswordDeliveryDetails(deliveryDetails.destination);
      }

      // 成功した場合のみ次のステップに進む
      setAuthStep("RESET_PASSWORD");
    } catch (error: unknown) {
      console.error("Reset password error:", error);
      // AuthErrorかname プロパティがあるエラーオブジェクトの場合
      if (error instanceof Error && "name" in error) {
        setError(getResetPasswordErrorMessage(error as Error & { name: string }));
      } else {
        setError("パスワードリセットの開始に失敗しました");
      }
      // エラーが発生した場合は画面遷移を行わず、エラーメッセージのみ表示
      // throw error; を削除してエラーを呼び出し元に伝播させない
    } finally {
      setIsLoading(false);
    }
  };

  // パスワードリセット確認処理
  const handleConfirmResetPassword = async (
    username: string,
    confirmationCode: string,
    newPassword: string
  ) => {
    try {
      setError(null);
      setIsLoading(true);

      console.info("Confirming password reset...");
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });

      console.info("Password reset confirmed successfully");
      setAuthStep("SIGN_IN");
      setResetPasswordDeliveryDetails(null);
    } catch (error: unknown) {
      console.error("Confirm reset password error:", error);
      if (error instanceof AuthError) {
        setError(error.message);
      } else {
        setError("パスワードリセットの確認に失敗しました");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * TOTP設定の検証
   * @param totpCode 6桁のTOTPコード
   * @returns void
   */
  const handleVerifyTOTPSetup = async (totpCode: string) => {
    try {
      setError(null);
      setIsLoading(true);

      // MFA設定中はconfirmSignInを使用してTOTPコードを検証
      await confirmSignIn({ challengeResponse: totpCode });
      // セッション確立のために1秒待機
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // 認証状態を再確認
      await checkAuthState();
    } catch (error: unknown) {
      console.error("TOTP setup verification error:", error);
      if (error instanceof AuthError) {
        // Code mismatchの場合は時刻同期の案内を追加
        if (
          error.message.includes("Code mismatch") ||
          error.name === "EnableSoftwareTokenMFAException"
        ) {
          setError("認証コードが一致しません。新しいコードで再度お試しください。");
        } else {
          setError("MFA設定に失敗しました。もう一度お試しください。");
        }
      } else {
        setError("予期しないエラーが発生しました。時間をおいて再度お試しください。");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * TOTPコードでサインイン確認
   * @param totpCode 6桁のTOTPコード
   * @returns void
   */
  const handleConfirmSignInWithTOTP = async (totpCode: string) => {
    try {
      setError(null);
      setIsLoading(true);

      await confirmSignIn({ challengeResponse: totpCode });
      // セッション確立のために1秒待機
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // 認証状態を再確認
      await checkAuthState();
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        // Code mismatchの場合は時刻同期の案内を追加
        if (error.message.includes("Code mismatch") || error.message.includes("Invalid code")) {
          setError("認証コードが一致しません。新しいコードで再度お試しください。");
        } else {
          setError("ログインに失敗しました。もう一度お試しください。");
        }
      } else {
        setError("予期しないエラーが発生しました。時間をおいて再度お試しください。");
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 初期化時に認証状態をチェック
  useEffect(() => {
    checkAuthState();
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    authStep,
    signIn: handleSignIn,
    confirmNewPassword: handleConfirmNewPassword,
    resetPassword: handleResetPassword,
    confirmResetPassword: handleConfirmResetPassword,
    confirmSignInWithTOTP: handleConfirmSignInWithTOTP,
    verifyTOTPSetup: handleVerifyTOTPSetup,
    updateName: handleUpdateName,
    signOut: handleSignOut,
    setAuthStep: setAuthStepHandler,
    error,
    resetPasswordDeliveryDetails,
    totpSetupUrl,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// 認証フックを作成
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// パスワード変更フォームコンポーネント
const NewPasswordForm: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("管理者"); // デフォルト値を設定
  const { confirmNewPassword, isLoading, error } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      alert("パスワードが一致しません");
      return;
    }

    if (newPassword.length < 12) {
      alert("パスワードは12文字以上で入力してください");
      return;
    }

    try {
      await confirmNewPassword(newPassword, name);
    } catch {
      // エラーは認証コンテキストで処理される
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card">
        {/* ヘッダーエリア */}
        <div className="auth-header">
          <h2 className="auth-title auth-title--password">パスワード設定</h2>
          <p className="auth-subtitle">初回ログインのため、新しいパスワードを設定してください</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="name" className="auth-label">
              表示名
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              placeholder="表示名を入力してください"
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="newPassword" className="auth-label">
              新しいパスワード
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              minLength={12}
              placeholder="12文字以上の新しいパスワード"
            />
          </div>

          <div className="auth-form-group--last">
            <label htmlFor="confirmPassword" className="auth-label">
              パスワード確認
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              minLength={12}
              placeholder="パスワードを再入力"
            />
            <div className="auth-password-hint">
              パスワードには12文字以上、大文字・小文字・数字・記号を含めてください
            </div>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" disabled={isLoading} className="auth-button">
            {isLoading ? (
              <span className="auth-loading-content">
                <div className="auth-loading-spinner" />
                設定中...
              </span>
            ) : (
              "パスワード設定"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

/**
 * MFA設定フォームコンポーネント
 * @returns React.JSX.Element
 */
const MFASetupForm: React.FC = () => {
  const { verifyTOTPSetup, isLoading, error, totpSetupUrl } = useAuth();

  /**
   * OTP入力完了時のハンドラー
   * @param code 6桁の認証コード
   */
  const handleOTPComplete = async (code: string) => {
    try {
      // TOTP設定の検証を実行
      await verifyTOTPSetup(code);
    } catch (error) {
      console.error("MFA setup verification failed:", error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card mfa-setup-card">
        {/* ヘッダーエリア */}
        <div className="auth-header">
          <h2 className="auth-title auth-title--mfa">MFA設定</h2>
          <p className="auth-subtitle">
            Authenticatorアプリで
            <br />
            以下の二次元コードをスキャンしてください
          </p>
        </div>

        <div className="mfa-setup-content">
          {totpSetupUrl && (
            <div className="qr-code-section">
              <QRCode value={totpSetupUrl} size={200} />
              <p className="mfa-help-text">
                Google Authenticatorや、
                <br />
                Microsoft Authenticatorなどのアプリで
                <br />
                この二次元コードをスキャンしてください
              </p>
              <div className="mfa-warning">
                Authenticatorアプリを必ずバックアップしてください
                <br />
                この二次元コードは一度しか表示されず、保存しても再利用できません
                <br />
                紛失・消去した場合、アカウントの復旧が困難になる可能性があります
              </div>
            </div>
          )}

          <div className="auth-form-group--last">
            <label className="auth-label">認証コード</label>
            <OTPInput onComplete={handleOTPComplete} disabled={isLoading} />
            <div className="auth-password-hint">
              Authenticatorアプリに表示された
              <br />
              {systemName}の6桁のコードを入力してください
            </div>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <div className="auth-button-wrapper">
            {isLoading && (
              <div className="auth-loading-indicator">
                <div className="auth-loading-spinner" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * TOTPコード入力フォームコンポーネント
 * @returns React.JSX.Element
 */
const TOTPCodeForm: React.FC = () => {
  const { confirmSignInWithTOTP, isLoading, error } = useAuth();

  /**
   * OTP入力完了時のハンドラー
   * @param code 6桁の認証コード
   */
  const handleOTPComplete = async (code: string) => {
    try {
      // TOTPコードでサインイン確認を実行
      await confirmSignInWithTOTP(code);
    } catch (error) {
      console.error("TOTP verification failed:", error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card">
        {/* ヘッダーエリア */}
        <div className="auth-header">
          <h2 className="auth-title auth-title--mfa">認証コード入力</h2>
          <p className="auth-subtitle">
            Authenticatorアプリに表示されている
            <br />
            6桁のコードを入力してください
          </p>
        </div>

        <div className="auth-form-group--last">
          <label className="auth-label">認証コード</label>
          <OTPInput onComplete={handleOTPComplete} disabled={isLoading} />
          <div className="auth-password-hint">
            Authenticatorアプリに表示された
            <br />
            {systemName}の6桁のコードを入力してください
          </div>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <div className="auth-button-wrapper">
          {isLoading && (
            <div className="auth-loading-indicator">
              <div className="auth-loading-spinner" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// パスワードを忘れた場合のフォームコンポーネント
const ForgotPasswordForm: React.FC = () => {
  const [username, setUsername] = useState("");
  const { resetPassword, isLoading, error, setAuthStep } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      alert("ユーザー名（メールアドレス）を入力してください");
      return;
    }

    try {
      await resetPassword(username);
    } catch {
      // エラーは認証コンテキストで処理される
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card">
        {/* ヘッダーエリア */}
        <div className="auth-header">
          <h2 className="auth-title auth-title--forgot">パスワードリセット</h2>
          <p className="auth-subtitle">登録されているメールアドレスにリセットコードを送信します</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="auth-form-group--last">
            <label htmlFor="username" className="auth-label">
              ユーザー名（メールアドレス）
            </label>
            <input
              id="username"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              placeholder="メールアドレスを入力"
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" disabled={isLoading} className="auth-button">
            {isLoading ? (
              <span className="auth-loading-content">
                <div className="auth-loading-spinner" />
                送信中...
              </span>
            ) : (
              "リセットコードを送信"
            )}
          </button>

          <button
            type="button"
            onClick={() => setAuthStep("SIGN_IN")}
            className="auth-link-button"
            disabled={isLoading}
          >
            ログイン画面に戻る
          </button>
        </form>
      </div>
    </div>
  );
};

// パスワードリセット確認フォームコンポーネント
const ResetPasswordForm: React.FC = () => {
  const [username, setUsername] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { confirmResetPassword, isLoading, error, setAuthStep, resetPasswordDeliveryDetails } =
    useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      alert("ユーザー名（メールアドレス）を入力してください");
      return;
    }

    if (!confirmationCode.trim()) {
      alert("確認コードを入力してください");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("パスワードが一致しません");
      return;
    }

    if (newPassword.length < 12) {
      alert("パスワードは12文字以上で入力してください");
      return;
    }

    try {
      await confirmResetPassword(username, confirmationCode, newPassword);
    } catch {
      // エラーは認証コンテキストで処理される
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card">
        {/* ヘッダーエリア */}
        <div className="auth-header">
          <h2 className="auth-title auth-title--reset">新しいパスワード設定</h2>
          <p className="auth-subtitle">
            {resetPasswordDeliveryDetails
              ? `${resetPasswordDeliveryDetails} に送信されたコードを入力してください`
              : "メールで送信されたコードを入力してください"}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="username" className="auth-label">
              ユーザー名（メールアドレス）
            </label>
            <input
              id="username"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              placeholder="メールアドレスを入力"
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="confirmationCode" className="auth-label">
              確認コード
            </label>
            <input
              id="confirmationCode"
              type="text"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              placeholder="メールで受信したコードを入力"
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="newPassword" className="auth-label">
              新しいパスワード
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              minLength={12}
              placeholder="12文字以上の新しいパスワード"
            />
          </div>

          <div className="auth-form-group--last">
            <label htmlFor="confirmPassword" className="auth-label">
              パスワード確認
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              minLength={12}
              placeholder="パスワードを再入力"
            />
            <div className="auth-password-hint">
              パスワードには12文字以上、大文字・小文字・数字・記号を含めてください
            </div>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" disabled={isLoading} className="auth-button">
            {isLoading ? (
              <span className="auth-loading-content">
                <div className="auth-loading-spinner" />
                設定中...
              </span>
            ) : (
              "パスワードを変更"
            )}
          </button>

          <button
            type="button"
            onClick={() => setAuthStep("SIGN_IN")}
            className="auth-link-button"
            disabled={isLoading}
          >
            ログイン画面に戻る
          </button>
        </form>
      </div>
    </div>
  );
};

// ログインフォームコンポーネント
export const LoginForm: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { signIn, isLoading, error, setAuthStep } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signIn(username, password);
    } catch {
      // エラーは認証コンテキストで処理される
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-card">
        {/* ヘッダーエリア */}
        <div className="auth-header">
          <h2 className="auth-title auth-title--login">{systemName}</h2>
          <p className="auth-subtitle">システムにアクセスするにはログインが必要です</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="username" className="auth-label">
              ユーザー名
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              placeholder="ユーザー名を入力"
            />
          </div>

          <div className="auth-form-group--last">
            <label htmlFor="password" className="auth-label">
              パスワード
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
              disabled={isLoading}
              placeholder="パスワードを入力"
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" disabled={isLoading} className="auth-button">
            {isLoading ? (
              <span className="auth-loading-content">
                <div className="auth-loading-spinner" />
                ログイン中...
              </span>
            ) : (
              "ログイン"
            )}
          </button>

          <button
            type="button"
            onClick={() => setAuthStep("FORGOT_PASSWORD")}
            className="auth-link-button"
            disabled={isLoading}
          >
            パスワードを忘れた方はこちら
          </button>
        </form>
      </div>
    </div>
  );
};

// 認証が必要なページを保護するコンポーネント
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, authStep } = useAuth();

  if (isLoading) {
    return (
      <div className="auth-loading-screen">
        <div>読み込み中...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authStep === "NEW_PASSWORD_REQUIRED") {
      return <NewPasswordForm />;
    }
    if (authStep === "MFA_SETUP") {
      return <MFASetupForm />;
    }
    if (authStep === "CONFIRM_SIGN_IN_WITH_TOTP") {
      return <TOTPCodeForm />;
    }
    if (authStep === "FORGOT_PASSWORD") {
      return <ForgotPasswordForm />;
    }
    if (authStep === "RESET_PASSWORD") {
      return <ResetPasswordForm />;
    }
    return <LoginForm />;
  }

  return <>{children}</>;
};
